import type { HistoryEvent } from '@pin/schemas/rest';
import type { ChatMessageAdmin } from '@pin/schemas/socket';
import { timer } from 'rxjs';

export type ChatMessage = ChatMessageAdmin & {
  redacted: boolean;
  banned: boolean;
  unbanned: boolean;
  /**
   * The end of the ban marked here, null for one without an end, so an
   * earlier ban's timer leaves it.
   */
  bannedUntil?: number | null;
  muted: boolean;
  unmuted: boolean;
  /** The end of the mute marked here, so an earlier mute's timer leaves it. */
  mutedUntil?: number;
  mutedUser: boolean;
  timeVisibleMs?: number;
  autoModerated?: boolean;
  /** Set by the severity pass: the categories the message hit. */
  severeCategories?: string[];
};

/**
 * How many messages the feed keeps. A two-hour stream sends over 12 000,
 * and a feed that kept them all stalled the browser for longer than
 * admin-api's 3 s ping timeout, so the socket kept dropping.
 */
export const FEED_LIMIT = 1_000;

export type ChatState = {
  messages: ChatMessage[];
  /**
   * Live messages push the oldest out past this, unless the feed is paused.
   * History that a moderator loads raises it, or the next live message
   * would push that out again.
   */
  limit: number;
  redactedMessageIds: Set<string>;
  automoderatedMessageIds: Set<string>;
  /** Severity categories by message id, for messages that arrive later. */
  severities: Map<string, string[]>;
  /**
   * The newest ban or unban, and the newest mute or unmute, of each user.
   * History can bring an older action that marks the message the newest
   * one should, or the message itself, and the newest then marks again.
   */
  latestActions: Map<string, UserAction>;
  earliestMessageId?: string;
};

type UserAction = Extract<
  HistoryEvent,
  { type: 'BAN_USER' | 'UNBAN_USER' | 'MUTE_USER' | 'UNMUTE_USER' }
>;

export const initialChatState: ChatState = {
  messages: [],
  limit: FEED_LIMIT,
  redactedMessageIds: new Set(),
  automoderatedMessageIds: new Set(),
  severities: new Map(),
  latestActions: new Map(),
  earliestMessageId: undefined,
};

/**
 * Whether the feed shows a message. Messages sent while muted stay in the
 * state, where they tell that the sender is muted, but moderators do not
 * want to see them.
 */
export const shownInFeed = (message: ChatMessage) => !message.mutedUser;

const compareRedisStreamId = (a: string, b: string): number => {
  const [aMilliSecondsTime, aSequenceNumber] = a.split('-').map(Number);
  const [bMilliSecondsTime, bSequenceNumber] = b.split('-').map(Number);
  if (aMilliSecondsTime !== bMilliSecondsTime) {
    return aMilliSecondsTime - bMilliSecondsTime;
  }
  return aSequenceNumber - bSequenceNumber;
};

export const reconstructStream = (
  state: ChatState,
  event: HistoryEvent,
  opts?: {
    prepend?: boolean;
    /**
     * Push the oldest messages out past the limit. Off while the feed is
     * paused, so nothing moves under a moderator reading or acting on it.
     */
    trim?: boolean;
  }
): ChatState => {
  // Because the earliest message can be an admin event that is not part of the
  // messages array.
  if (
    state.earliestMessageId == null ||
    compareRedisStreamId(event.id, state.earliestMessageId) < 0
  ) {
    state.earliestMessageId = event.id;
  }

  switch (event.type) {
    case 'CHAT_MESSAGE':
    case 'MUTED_CHAT_MESSAGE': {
      // The socket connects before the first history page returns, so the
      // newest messages can arrive twice. A repeated id breaks the feed's
      // keyed each block.
      if (state.messages.some((m) => m.id === event.id)) break;
      const msg: ChatMessage = {
        ...event,
        type: 'CHAT_MESSAGE',
        redacted: state.redactedMessageIds.has(event.id),
        autoModerated: state.automoderatedMessageIds.has(event.id),
        severeCategories: state.severities.get(event.id),
        banned: false,
        unbanned: false,
        muted: false,
        unmuted: false,
        mutedUser: event.type === 'MUTED_CHAT_MESSAGE',
      };
      const insertIdx = opts?.prepend
        ? state.messages.findIndex(
            (m) => compareRedisStreamId(m.id, msg.id) > 0
          )
        : -1;
      if (insertIdx === -1) {
        state.messages.push(msg);
      } else {
        state.messages.splice(insertIdx, 0, msg);
      }
      for (const kind of ['ban', 'mute']) {
        const latest = state.latestActions.get(`${kind}:${msg.user.id}`);
        if (latest != null && compareRedisStreamId(msg.id, latest.id) < 0) {
          markUser(state.messages, msg.user.id, latest);
        }
      }
      if (opts?.prepend) {
        state.limit = Math.max(state.limit, state.messages.length);
      } else if (opts?.trim && state.messages.length > state.limit) {
        state.messages.splice(0, state.messages.length - state.limit);
        // History picks up where the feed now starts, not where it started
        state.earliestMessageId = state.messages[0]?.id;
      }
      break;
    }

    case 'REDACT_MESSAGE': {
      for (const id of event.messageIds) {
        state.redactedMessageIds.add(id);
      }
      for (const m of state.messages) {
        if (!event.messageIds.includes(m.id)) continue;
        const visibleMs = event.ts - m.ts;
        m.redacted = true;
        m.autoModerated = event.role === 'automoderator';
        m.timeVisibleMs =
          m.timeVisibleMs != null
            ? Math.min(m.timeVisibleMs, visibleMs)
            : visibleMs;
      }
      break;
    }
    case 'MODERATION_SEVERITY': {
      state.severities.set(event.messageId, event.categories);
      const m = state.messages.find((m) => m.id === event.messageId);
      if (m != null) m.severeCategories = event.categories;
      break;
    }
    case 'BAN_USER':
    case 'UNBAN_USER':
    case 'MUTE_USER':
    case 'UNMUTE_USER': {
      const kind = event.type.endsWith('BAN_USER') ? 'ban' : 'mute';
      for (const userId of event.userIds) {
        markUser(state.messages, userId, event);
        const key = `${kind}:${userId}`;
        const latest = state.latestActions.get(key);
        if (latest == null || compareRedisStreamId(latest.id, event.id) <= 0) {
          state.latestActions.set(key, event);
        } else {
          // An older action from history
          markUser(state.messages, userId, latest);
        }
      }
      break;
    }
  }
  return state;
};

/** Marks what an action did on the user's messages from before it. */
const markUser = (
  messages: ChatMessage[],
  userId: string,
  action: UserAction
) => {
  const mark = (cb: (msg: ChatMessage) => void) =>
    updateLastMessageBeforeEvent(messages, userId, action.id, cb);
  switch (action.type) {
    case 'BAN_USER': {
      const { expiresAt } = action;
      const expiresIn = expiresAt != null ? expiresAt - Date.now() : null;
      mark((msg) => {
        if (expiresIn != null && expiresIn <= 0) {
          msg.banned = false;
          msg.unbanned = true;
          msg.bannedUntil = undefined;
          return;
        }
        msg.banned = true;
        msg.unbanned = false;
        msg.bannedUntil = expiresAt;
        if (expiresIn != null) {
          timer(expiresIn).subscribe(() => {
            if (msg.bannedUntil !== expiresAt) return;
            msg.banned = false;
            msg.unbanned = true;
          });
        }
      });
      break;
    }
    case 'UNBAN_USER': {
      mark((msg) => {
        msg.banned = false;
        msg.unbanned = true;
        msg.bannedUntil = undefined;
      });
      break;
    }
    case 'MUTE_USER': {
      const { expiresAt } = action;
      const expiresIn = expiresAt - Date.now();
      mark((msg) => {
        if (expiresIn <= 0) {
          msg.muted = false;
          msg.unmuted = true;
          msg.mutedUntil = undefined;
          return;
        }
        msg.muted = true;
        msg.unmuted = false;
        msg.mutedUntil = expiresAt;
        timer(expiresIn).subscribe(() => {
          if (msg.mutedUntil !== expiresAt) return;
          msg.muted = false;
          msg.unmuted = true;
        });
      });
      break;
    }
    case 'UNMUTE_USER': {
      mark((msg) => {
        msg.muted = false;
        msg.unmuted = true;
        msg.mutedUntil = undefined;
      });
      break;
    }
  }
};

/**
 * Marks the user's last message before the event, which `isUserMuted`
 * reads, and the last one the feed shows if that is another, so moderators
 * see the mark. A muted user's last message is often one sent while muted.
 */
const updateLastMessageBeforeEvent = (
  messages: ChatMessage[],
  userId: string,
  eventId: string,
  cb: (msg: ChatMessage) => void
) => {
  let last: ChatMessage | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.user.id !== userId || compareRedisStreamId(msg.id, eventId) >= 0) {
      continue;
    }
    last ??= msg;
    if (shownInFeed(msg)) {
      cb(msg);
      break;
    }
  }
  if (last != null && !shownInFeed(last)) {
    cb(last);
  }
};
